import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Clock3, MapPinned, Share2, AlertCircle } from "lucide-react";
import { useData } from "../contexts/DataContext";
import { Logo } from "../components/Logo";
import { MapPanel } from "../components/MapPanel";
import { formatDateTime, getStatusLabel } from "../lib/utils";

// Helper function to format name as "First L."
const formatPrivacyName = (fullName) => {
  if (!fullName) return "Student";
  const parts = fullName.trim().split(" ");
  if (parts.length === 1) return parts[0];
  const firstName = parts[0];
  const lastInitial = parts[parts.length - 1][0];
  return `${firstName} ${lastInitial}.`;
};

export default function PublicTrackingPage() {
  const { token } = useParams();
  const { getTrackingByToken } = useData();
  const [tracking, setTracking] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;

    const loadTracking = async () => {
      const result = await getTrackingByToken(token);
      if (!active) return;
      if (result.success) {
        setTracking(result.data);
      }
      setLoading(false);
    };

    loadTracking();
    const interval = window.setInterval(loadTracking, 10000);
    return () => {
      active = false;
      window.clearInterval(interval);
    };
  }, [token, getTrackingByToken]);

  const markers = useMemo(() => (tracking?.location ? [tracking.location] : []), [tracking]);

  if (loading) {
    return <div className="flex min-h-screen items-center justify-center bg-[#F5F7FA] text-[#64748B]">Loading live tracking…</div>;
  }

  if (!tracking) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#F5F7FA] px-4">
        <div className="w-full max-w-md rounded-[24px] border border-[#E2E8F0] bg-white p-8 text-center shadow-sm">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-amber-50">
            <AlertCircle className="text-amber-600" size={32} />
          </div>
          <h2 className="mt-4 font-['Manrope'] text-xl font-semibold text-[#0F172A]">Tracking link unavailable</h2>
          <p className="mt-2 text-sm text-[#64748B]">
            This tracking link may have expired or the trip has been completed. Please contact your transport coordinator for assistance.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F7FA] px-4 py-6 sm:px-6 sm:py-8">
      <div className="mx-auto max-w-6xl space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-4 rounded-[24px] sm:rounded-[32px] border border-[#E2E8F0] bg-white p-4 sm:p-6 shadow-sm">
          <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between">
            <Logo size="large" />
            <div className="rounded-xl sm:rounded-2xl border border-[#E2E8F0] bg-[#F8FAFC] px-3 py-2 sm:px-4 sm:py-3 text-xs sm:text-sm text-[#475569]">
              🔒 Secure live tracking
            </div>
          </div>
          <div className="rounded-xl border border-amber-100 bg-amber-50 p-3 text-xs sm:text-sm text-amber-800">
            <p className="font-semibold">Link expiry notice</p>
            <p className="mt-1">This tracking link will expire once the trip is completed. Bookmark this page for live updates during the journey.</p>
          </div>
        </div>

        {/* Mobile-first layout: Stack on small screens, side-by-side on larger */}
        <div className="grid gap-4 sm:gap-6 lg:grid-cols-[1.1fr_0.9fr]">
          {/* Map and status cards */}
          <div className="space-y-4 sm:space-y-6">
            <MapPanel markers={markers} height={340} title="Live vehicle location" />
            
            {/* Status cards - Stack on mobile, grid on tablet+ */}
            <div className="grid gap-3 sm:grid-cols-3 sm:gap-4">
              <div className="guardian-card bg-white p-4">
                <div className="flex items-center gap-2 text-[#163D6B]">
                  <MapPinned size={16} className="shrink-0" />
                  <p className="text-xs sm:text-sm font-semibold text-[#0F172A]">Trip status</p>
                </div>
                <p className="mt-2 text-xl sm:text-2xl font-semibold text-[#0F172A]">{getStatusLabel(tracking.trip.status)}</p>
              </div>
              <div className="guardian-card bg-white p-4">
                <div className="flex items-center gap-2 text-[#163D6B]">
                  <Clock3 size={16} className="shrink-0" />
                  <p className="text-xs sm:text-sm font-semibold text-[#0F172A]">ETA</p>
                </div>
                <p className="mt-2 text-xl sm:text-2xl font-semibold text-[#0F172A]">
                  {tracking.trip.eta_minutes ? `${tracking.trip.eta_minutes} min` : "Calculating..."}
                </p>
              </div>
              <div className="guardian-card bg-white p-4">
                <div className="flex items-center gap-2 text-[#163D6B]">
                  <Share2 size={16} className="shrink-0" />
                  <p className="text-xs sm:text-sm font-semibold text-[#0F172A]">Last updated</p>
                </div>
                <p className="mt-2 text-xs sm:text-sm font-semibold text-[#0F172A]">
                  {tracking.location?.timestamp ? formatDateTime(tracking.location.timestamp) : "No update yet"}
                </p>
              </div>
            </div>
          </div>

          {/* Journey details card */}
          <div className="guardian-card bg-white p-5 sm:p-6">
            <h1 className="font-['Manrope'] text-2xl sm:text-3xl font-semibold text-[#0F172A]">
              {formatPrivacyName(tracking.trip.child_name)}
            </h1>
            <p className="mt-1 text-sm text-[#64748B]">Live journey tracking</p>
            
            <dl className="mt-6 space-y-4 text-sm">
              <div className="rounded-xl border border-[#E2E8F0] bg-[#F8FAFC] p-4">
                <dt className="text-xs font-semibold uppercase tracking-wide text-[#64748B]">Pickup location</dt>
                <dd className="mt-1 font-medium text-[#0F172A]">{tracking.trip.pickup_location}</dd>
              </div>
              <div className="rounded-xl border border-[#E2E8F0] bg-[#F8FAFC] p-4">
                <dt className="text-xs font-semibold uppercase tracking-wide text-[#64748B]">Destination</dt>
                <dd className="mt-1 font-medium text-[#0F172A]">{tracking.trip.dropoff_location}</dd>
              </div>
              <div className="rounded-xl border border-[#E2E8F0] bg-[#F8FAFC] p-4">
                <dt className="text-xs font-semibold uppercase tracking-wide text-[#64748B]">Driver</dt>
                <dd className="mt-1 font-medium text-[#0F172A]">{tracking.trip.driver_name || "Assigned driver"}</dd>
              </div>
              <div className="rounded-xl border border-[#E2E8F0] bg-[#F8FAFC] p-4">
                <dt className="text-xs font-semibold uppercase tracking-wide text-[#64748B]">Vehicle</dt>
                <dd className="mt-1 font-medium text-[#0F172A]">{tracking.trip.vehicle_registration || "Assigned vehicle"}</dd>
              </div>
            </dl>

            {/* Footer info */}
            <div className="mt-6 rounded-xl border border-[#E2E8F0] bg-[#F8FAFC] p-4 text-xs text-[#475569]">
              <p className="font-semibold text-[#0F172A]">Live updates</p>
              <p className="mt-1">This page refreshes automatically every 10 seconds. No action needed.</p>
            </div>
          </div>
        </div>

        {/* Footer - company info */}
        <div className="rounded-[24px] border border-[#E2E8F0] bg-white p-4 text-center text-xs text-[#64748B]">
          <p className="font-semibold text-[#0F172A]">Powered by CareFleet Transport Ltd</p>
          <p className="mt-1">Safeguarding-focused transport management platform</p>
        </div>
      </div>
    </div>
  );
}
